import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { UploadCloud, FileJson, FileArchive, ClipboardType, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

// Import characters and chats from other chatbot platforms.
// Three sources supported by the backend:
//   • Tavern Character Card V2  → /api/user/import/tavern  (JSON or PNG)
//   • Character.AI data dump    → /api/user/import/cai     (ZIP)
//   • Free-form paste           → /api/user/import/paste   (text, structured by Claude)
export default function ImportFromOtherPlatforms({ onImported }) {
  const [mode, setMode] = useState("tavern");  // 'tavern' | 'cai' | 'paste'
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [paste, setPaste] = useState("");
  const tavernRef = useRef(null);
  const caiRef = useRef(null);

  async function uploadFile(endpoint, file, successMessage) {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    setLastResult(null);
    try {
      const { data } = await api.post(endpoint, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180_000,
      });
      setLastResult(data);
      toast.success(successMessage(data));
      onImported?.(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Import failed.");
    } finally {
      setBusy(false);
    }
  }

  async function submitPaste() {
    if (paste.trim().length < 20) {
      toast.error("Paste at least a short character sheet (20+ characters).");
      return;
    }
    setBusy(true);
    setLastResult(null);
    try {
      const { data } = await api.post("/user/import/paste", { text: paste }, { timeout: 60_000 });
      setLastResult(data);
      toast.success(
        data.duplicate
          ? `"${data.name}" already exists in your library — opened the existing one.`
          : `Created "${data.name}" — you can edit it now.`
      );
      setPaste("");
      onImported?.(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't parse that paste.");
    } finally {
      setBusy(false);
    }
  }

  const modeCard = (id, Icon, title, sub) => (
    <button
      key={id}
      type="button"
      data-testid={`import-mode-${id}`}
      onClick={() => setMode(id)}
      className={`flex flex-col items-start gap-1 rounded-xl border p-3 text-left transition ${
        mode === id
          ? "border-violet-400/60 bg-violet-500/10"
          : "border-white/10 bg-white/[0.02] hover:bg-white/[0.05]"
      }`}
    >
      <Icon size={16} className={mode === id ? "text-violet-200" : "text-neutral-400"} />
      <div className="text-xs font-medium text-white">{title}</div>
      <div className="text-[10px] leading-tight text-neutral-400">{sub}</div>
    </button>
  );

  return (
    <section
      className="mt-6 rounded-2xl border border-white/10 bg-white/[0.02] p-4"
      data-testid="profile-cross-import-section"
    >
      <div className="mb-2 text-[10px] uppercase tracking-widest text-neutral-500">
        Import from other platforms
      </div>
      <p className="text-sm text-neutral-300">
        Bring characters over from Character.AI, Chub.ai, JanitorAI, SillyTavern, and more.
        All imports land in your library as private and SFW — you can toggle either later.
      </p>

      <div className="mt-3 grid grid-cols-3 gap-2">
        {modeCard("tavern", FileJson, "Character card", "Chub · Venus · JanitorAI · SillyTavern (.json or .png)")}
        {modeCard("cai", FileArchive, "Character.AI dump", "The .zip c.ai emails you from 'Download my data'")}
        {modeCard("paste", ClipboardType, "Paste & structure", "Any format · Claude builds the fields")}
      </div>

      {/* Uploader body */}
      <div className="mt-4">
        {mode === "tavern" && (
          <>
            <input
              ref={tavernRef}
              type="file"
              accept=".json,application/json,.png,image/png"
              className="hidden"
              data-testid="import-tavern-file"
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = "";
                uploadFile("/user/import/tavern", f, (d) =>
                  d.duplicate
                    ? `"${d.name}" already existed — reused it.`
                    : `Imported "${d.name}" — private + SFW.`
                );
              }}
            />
            <button
              type="button"
              data-testid="import-tavern-pick-btn"
              disabled={busy}
              onClick={() => tavernRef.current?.click()}
              className="btn-pill bg-violet-500/15 border border-violet-500/30 text-violet-100 hover:bg-violet-500/25 disabled:opacity-40"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <UploadCloud size={14} />}
              {busy ? "Importing…" : "Choose card file"}
            </button>
            <p className="mt-2 text-[11px] text-neutral-500">
              Drop a Character Card V2 <code className="text-neutral-400">.json</code> or a{" "}
              <code className="text-neutral-400">.png</code> with embedded card metadata.
            </p>
          </>
        )}

        {mode === "cai" && (
          <>
            <input
              ref={caiRef}
              type="file"
              accept=".zip,application/zip"
              className="hidden"
              data-testid="import-cai-file"
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = "";
                uploadFile("/user/import/cai", f, (d) => {
                  const s = d.stats || {};
                  return `Imported from Character.AI — ${s.characters || 0} characters, ${s.chats || 0} chats, ${s.messages || 0} messages.`;
                });
              }}
            />
            <button
              type="button"
              data-testid="import-cai-pick-btn"
              disabled={busy}
              onClick={() => caiRef.current?.click()}
              className="btn-pill bg-violet-500/15 border border-violet-500/30 text-violet-100 hover:bg-violet-500/25 disabled:opacity-40"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <UploadCloud size={14} />}
              {busy ? "Extracting…" : "Choose c.ai ZIP"}
            </button>
            <p className="mt-2 text-[11px] leading-relaxed text-neutral-500">
              Get the file from Character.AI → <em>Settings → Privacy → Download my data</em>.
              They'll email you a link within ~24 hours. Upload the ZIP as-is.
            </p>
          </>
        )}

        {mode === "paste" && (
          <>
            <textarea
              data-testid="import-paste-textarea"
              value={paste}
              onChange={(e) => setPaste(e.target.value)}
              placeholder="Paste anything — a Character.AI card, JanitorAI page, forum bot sheet, plain description. Claude will structure it into a character."
              rows={7}
              className="input-field w-full resize-y text-sm"
              disabled={busy}
            />
            <div className="mt-2 flex items-center justify-between">
              <span className="text-[11px] text-neutral-500">
                {paste.length.toLocaleString()} chars · Claude Sonnet 4.6 parses it
              </span>
              <button
                type="button"
                data-testid="import-paste-submit-btn"
                onClick={submitPaste}
                disabled={busy || paste.trim().length < 20}
                className="btn-pill bg-violet-500/15 border border-violet-500/30 text-violet-100 hover:bg-violet-500/25 disabled:opacity-40"
              >
                {busy ? <Loader2 size={14} className="animate-spin" /> : <UploadCloud size={14} />}
                {busy ? "Parsing…" : "Import"}
              </button>
            </div>
          </>
        )}
      </div>

      {lastResult && (
        <div
          data-testid="import-result-summary"
          className="mt-4 flex items-start gap-2 rounded-xl border border-emerald-400/30 bg-emerald-500/5 p-3 text-xs text-emerald-200"
        >
          <CheckCircle2 size={16} className="mt-0.5 shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="font-medium">Import complete</div>
            {lastResult.stats ? (
              <div className="mt-0.5 text-emerald-300/80">
                {lastResult.stats.characters} characters · {lastResult.stats.chats} chats · {lastResult.stats.messages} messages
                {lastResult.stats.personas ? ` · ${lastResult.stats.personas} personas` : ""}
                {lastResult.stats.skipped_chats ? ` · ${lastResult.stats.skipped_chats} skipped` : ""}
              </div>
            ) : (
              <div className="mt-0.5 text-emerald-300/80">
                {lastResult.duplicate ? `"${lastResult.name}" already existed.` : `Created "${lastResult.name}".`}
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
